import { ParsedUrlQuery } from 'querystring';

import {
  DURATION_TIME,
  FLIGHT_SEARCH_QUERY_PARAM_KEYS,
} from '@components/views/flight/constants';
import {
  FlightGuestProps,
  FlightTripType,
} from '@components/views/flight/types';
import {
  convertDateToMomentObject,
  DEFAULT_DATE_TIME_FORMAT,
} from '@utils/dateUtil';
import { ListingFilterItemKey } from '@components/ui/filters/MultiSelectionFilter/types';
import {
  AirportSearchViewModel,
  FlightNodeViewModel,
  FlightPrebookAddTravelerArrayViewModel,
  FlightViewModel,
} from '@api/index';
import { TURKEY_COUNTRY_CODE } from '@constants/constants';

export const getDurationTime = (
  durationTime: string,
  shortName: boolean
): string => {
  const durationTimeArray = durationTime?.match(/.{2}/g);
  let durationTimeText = '';
  if (durationTimeArray?.length === 2) {
    const totalHour = parseInt(durationTimeArray[0]);
    const totalMinute = parseInt(durationTimeArray[1]);
    if (totalHour) {
      durationTimeText += totalHour
        ? `${totalHour} ${
            !shortName ? DURATION_TIME.hour : DURATION_TIME.shortHour
          } `
        : '';
    }
    if (totalMinute) {
      durationTimeText += totalMinute
        ? `${totalMinute} ${
            !shortName ? DURATION_TIME.minute : DURATION_TIME.shortMinute
          }`
        : '';
    }
  }
  return durationTimeText;
};

export const convertDurationTimeToMinute = (durationTime: string): number => {
  const durationTimeArray = durationTime?.match(/.{2}/g);
  if (durationTimeArray?.length === 2) {
    const totalHour = parseInt(durationTimeArray[0]);
    const totalMinute = parseInt(durationTimeArray[1]);

    return totalHour * 60 + totalMinute;
  }
  return -1;
};

export const getRemainingTime = (fromSegment: any, toSegment: any) => {
  const momentArrDate = convertDateToMomentObject(
    fromSegment.arrDate + ' ' + fromSegment.arrTime,
    DEFAULT_DATE_TIME_FORMAT
  );
  const momentDepDate = convertDateToMomentObject(
    toSegment.depDate + ' ' + toSegment.depTime,
    DEFAULT_DATE_TIME_FORMAT
  );

  let hourDiff = momentDepDate.diff(momentArrDate, 'hours').toString();
  let minuteDiff = (
    momentDepDate.diff(momentArrDate, 'minutes') % 60
  ).toString();

  hourDiff = hourDiff.length === 1 ? '0' + hourDiff : hourDiff;
  minuteDiff = minuteDiff.length === 1 ? '0' + minuteDiff : minuteDiff;

  return hourDiff + minuteDiff;
};

export const getIsArrivalNextDay = ({
  arrTime,
  arrDate,
  depTime,
  depDate,
}: {
  arrTime: string;
  arrDate: string;
  depTime: string;
  depDate: string;
}) => {
  const momentArrDate = convertDateToMomentObject(
    arrDate + ' ' + arrTime,
    DEFAULT_DATE_TIME_FORMAT
  );
  const momentDepDate = convertDateToMomentObject(
    depDate + ' ' + depTime,
    DEFAULT_DATE_TIME_FORMAT
  );
  return !momentArrDate.isSame(momentDepDate, 'day');
};

export const compareFlightDateTime = (
  first: { time: string; date: string },
  second: { time: string; date: string }
) => {
  const momentFirst = convertDateToMomentObject(
    first.date + ' ' + first.time,
    DEFAULT_DATE_TIME_FORMAT
  );
  const momentSecond = convertDateToMomentObject(
    second.date + ' ' + second.time,
    DEFAULT_DATE_TIME_FORMAT
  );

  return momentFirst.isBefore(momentSecond, 'm');
};

export const getGuestText = (
  {
    numberOfAdultGuests,
    numberOfChildGuests,
    numberOfInfantGuests,
  }: FlightGuestProps = {} as FlightGuestProps
) => {
  return `${numberOfAdultGuests} Yetişkin${
    numberOfChildGuests ? ', ' + numberOfChildGuests + ' Çocuk' : ''
  }${numberOfInfantGuests ? ', ' + numberOfInfantGuests + ' Bebek ' : ''}`;
};

export const getAirportListItemTitleText = ({
  isCity,
  airportCode,
  airportName,
  cityName,
  countryName,
}: AirportSearchViewModel) =>
  isCity ? cityName + ', ' + countryName : airportName + ', ' + airportCode;

export const getAirportListItemSubTitleText = ({
  isCity,
  airportName,
  cityName,
  countryName,
}: AirportSearchViewModel) =>
  isCity ? airportName : cityName + ', ' + countryName;

export const validateFormField = (item: unknown) => (item ? 'val' : undefined);

export const getDateTimeFromFlightInfo = (dateStr: string, timeStr: string) => {
  return convertDateToMomentObject(
    dateStr + ' ' + timeStr,
    DEFAULT_DATE_TIME_FORMAT
  );
};

export const generateQueryFromListingFilterItems = (
  existingQuery: ParsedUrlQuery,
  queryKey: string,
  listingFilterItems: ListingFilterItemKey[]
): void => {
  if (listingFilterItems.length) {
    existingQuery[queryKey] = listingFilterItems.join(',');
  } else {
    delete existingQuery[queryKey];
  }
};

export const generateFlightSearchQuery = (
  existingQuery: ParsedUrlQuery,
  queryKey: string,
  value?: string
): void => {
  if (value) {
    existingQuery[queryKey] = value;
  } else {
    delete existingQuery[queryKey];
  }
};

export const getFlightTripType = (
  query: ParsedUrlQuery
): FlightTripType | undefined => {
  const isOneWay = query[FLIGHT_SEARCH_QUERY_PARAM_KEYS.isOneWay];
  if (isOneWay !== undefined) {
    return isOneWay === 'true' ? 'one-way' : 'round-trip';
  }
};

export const getFlightPrebookTravelersFromGuestInfo = ({
  adultCount = 0,
  childCount = 0,
  infantCount = 0,
}: {
  adultCount: number;
  childCount?: number;
  infantCount?: number;
}): FlightPrebookAddTravelerArrayViewModel[] => {
  const travelers: FlightPrebookAddTravelerArrayViewModel[] = [];
  Array(adultCount)
    .fill(0)
    .forEach(() => travelers.push({ passengerTypeCode: 'ADT' }));
  Array(childCount)
    .fill(0)
    .forEach(() => travelers.push({ passengerTypeCode: 'CHD' }));
  Array(infantCount)
    .fill(0)
    .forEach(() => travelers.push({ passengerTypeCode: 'INF' }));

  return travelers;
};

const isAirportInTurkey = (airport?: AirportSearchViewModel) =>
  !airport || airport.countryCode === TURKEY_COUNTRY_CODE;

export const isDomesticFlight = (
  departureAirport?: AirportSearchViewModel,
  arrivalAirport?: AirportSearchViewModel
): boolean => {
  const value =
    isAirportInTurkey(departureAirport) && isAirportInTurkey(arrivalAirport);
  return value;
};

export const toFixedFloat = (amount: number): number => {
  return parseFloat(amount.toFixed(2));
};

export const generateOrderFlights = (
  flights: FlightViewModel[]
):
  | {
      lowestPriceFlight: FlightViewModel;
      shortestTimeFlight: FlightViewModel;
      earliestDepartureTimeFlight: FlightViewModel;
      earliestArrivalTimeFlight: FlightViewModel;
    }
  | undefined => {
  if (flights && flights.length) {
    let lowestPriceFlight: FlightViewModel = flights[0];
    let shortestTimeFlight: FlightViewModel = flights[0];
    let earliestDepartureTimeFlight: FlightViewModel = flights[0];
    let earliestArrivalTimeFlight: FlightViewModel = flights[0];
    let shortestTime = convertDurationTimeToMinute(
      lowestPriceFlight.flights[0].durationTime
    );

    flights.forEach((flight) => {
      const price =
        flight.flights[0].fareGroup.slice(-1)[0]?.fares.fareGroupCode ===
          'BU' || flight.flights[0].segments[0].cabinName === 'Business'
          ? flight.flights[0].price
          : flight.price;

      const lowestPriceFlightPrice =
        lowestPriceFlight.flights[0].fareGroup.slice(-1)[0]?.fares
          .fareGroupCode === 'BU' ||
        flight.flights[0].segments[0].cabinName === 'Business'
          ? lowestPriceFlight.flights[0].price
          : lowestPriceFlight.price;

      if (price < lowestPriceFlightPrice) {
        lowestPriceFlight = flight;
      }

      const durationTimeAsMinute = convertDurationTimeToMinute(
        flight.flights[0].durationTime
      );

      if (durationTimeAsMinute < shortestTime) {
        shortestTimeFlight = flight;
        shortestTime = durationTimeAsMinute;
      }
      if (
        compareFlightDateTime(
          {
            time: flight.flights[0].segments[0].depTime,
            date: flight.flights[0].segments[0].depDate,
          },
          {
            time: earliestDepartureTimeFlight.flights[0].segments[0].depTime,
            date: earliestDepartureTimeFlight.flights[0].segments[0].depDate,
          }
        )
      ) {
        earliestDepartureTimeFlight = flight;
      }
      if (
        compareFlightDateTime(
          {
            time: flight.flights[0].segments[
              flight.flights[0].segments.length - 1
            ].arrTime,
            date: flight.flights[0].segments[
              flight.flights[0].segments.length - 1
            ].arrDate,
          },
          {
            time: earliestArrivalTimeFlight.flights[0].segments[
              earliestArrivalTimeFlight.flights[0].segments.length - 1
            ].arrTime,
            date: earliestArrivalTimeFlight.flights[0].segments[
              earliestArrivalTimeFlight.flights[0].segments.length - 1
            ].arrDate,
          }
        )
      ) {
        earliestArrivalTimeFlight = flight;
      }
    });

    if (
      shortestTimeFlight.flights[0].fareGroup.slice(-1)[0]?.fares
        .fareGroupCode === 'BU'
    ) {
      shortestTimeFlight.price = shortestTimeFlight.flights[0].price;
    }

    if (
      earliestDepartureTimeFlight.flights[0].fareGroup.slice(-1)[0]?.fares
        .fareGroupCode === 'BU'
    ) {
      earliestDepartureTimeFlight.price =
        earliestDepartureTimeFlight.flights[0].price;
    }

    if (
      earliestArrivalTimeFlight.flights[0].fareGroup.slice(-1)[0]?.fares
        .fareGroupCode === 'BU'
    ) {
      earliestArrivalTimeFlight.price =
        earliestArrivalTimeFlight.flights[0].price;
    }

    return {
      lowestPriceFlight,
      shortestTimeFlight,
      earliestDepartureTimeFlight,
      earliestArrivalTimeFlight,
    };
  }
};

export const orderFlights = (
  flights: FlightViewModel[],
  activeOrderKey: string
) => {
  if (activeOrderKey === 'lowest-price') {
    const index = flights.findIndex(
      (flight) =>
        flight.flights[0].fareGroup.slice(-1)[0]?.fares.fareGroupCode === 'BU'
    );

    if (index !== -1) {
      flights.sort((firstFlight, secondFlight) => {
        return firstFlight.flights[0].price - secondFlight.flights[0].price;
      });
    } else {
      flights.sort((firstFlight, secondFlight) => {
        return firstFlight.price - secondFlight.price;
      });
    }
  } else if (activeOrderKey === 'shortest-time') {
    flights.sort((firstFlight, secondFlight) => {
      return (
        convertDurationTimeToMinute(firstFlight.flights[0].durationTime) -
        convertDurationTimeToMinute(secondFlight.flights[0].durationTime)
      );
    });
  } else if (activeOrderKey === 'departure-time') {
    flights.sort((firstFlight, secondFlight) => {
      return compareFlightDateTime(
        {
          time: firstFlight.flights[0].segments[0].depTime,
          date: firstFlight.flights[0].segments[0].depDate,
        },
        {
          time: secondFlight.flights[0].segments[0].depTime,
          date: secondFlight.flights[0].segments[0].depDate,
        }
      )
        ? -1
        : 1;
    });
  } else if (activeOrderKey === 'arrival-time') {
    flights.sort((firstFlight, secondFlight) => {
      return compareFlightDateTime(
        {
          time: firstFlight.flights[0].segments[
            firstFlight.flights[0].segments.length - 1
          ].arrTime,
          date: firstFlight.flights[0].segments[
            firstFlight.flights[0].segments.length - 1
          ].arrDate,
        },
        {
          time: secondFlight.flights[0].segments[
            secondFlight.flights[0].segments.length - 1
          ].arrTime,
          date: secondFlight.flights[0].segments[
            secondFlight.flights[0].segments.length - 1
          ].arrDate,
        }
      )
        ? -1
        : 1;
    });
  }
};

export const getServiceFee = ({
  outboundFlight,
  inboundFlight,
  guestCount,
}: {
  outboundFlight: FlightNodeViewModel;
  inboundFlight?: FlightNodeViewModel;
  guestCount: number;
}): number => {
  let serviceFeeAmount = outboundFlight.fares.serviceFare?.amount || 0;

  if (inboundFlight) {
    if (outboundFlight.provider !== inboundFlight.provider) {
      serviceFeeAmount += inboundFlight?.fares.serviceFare?.amount || 0;
    }
  }

  return serviceFeeAmount * guestCount;
};

export const getFlightTotalPriceWithoutFee = (flight: FlightNodeViewModel) =>
  flight.fares.total || 0;

export const getFlightTotalMilePriceWithoutFee = (
  flight: FlightNodeViewModel
) => flight.fares.milePrice || 0;
